
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { History, Calendar, TrendingUp, Brain, FileText, Search, Filter, Sparkles, Clock, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RepositoryFactory } from '@/shared/infrastructure/RepositoryFactory';
import { getPersonalityInsights } from '@/services/personalityInsightService';
import EnhancedAdvancedAnalysisModal from './EnhancedAdvancedAnalysisModal';

export default function PreviousResultsButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('recent');
  
  const repositoryFactory = RepositoryFactory.getInstance();
  const personalityRepository = repositoryFactory.getPersonalityRepository();

  const { data: previousResults, isLoading } = useQuery({
    queryKey: ['previous-big-five-results'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];
      
      return await personalityRepository.findByUserId(session.user.id);
    },
    enabled: isOpen
  });

  const { data: insights } = useQuery({
    queryKey: ['personality-insights'],
    queryFn: getPersonalityInsights,
    enabled: isOpen
  });

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPersonalityTrait = (trait: string, value: number) => {
    const percentage = Math.round(value * 20);
    let level = 'Baixo';
    let color = 'bg-red-100 text-red-800';
    
    if (percentage >= 70) {
      level = 'Alto';
      color = 'bg-green-100 text-green-800';
    } else if (percentage >= 40) {
      level = 'Médio';
      color = 'bg-yellow-100 text-yellow-800';
    }
    
    return { level, color, percentage };
  };

  const traitNames = {
    openness: 'Abertura',
    conscientiousness: 'Conscienciosidade',
    extraversion: 'Extroversão',
    agreeableness: 'Amabilidade',
    neuroticism: 'Neuroticismo'
  };

  const handleViewAnalysis = (insight: any) => {
    setSelectedAnalysis(insight.analysis_content);
    setSelectedInsight({
      id: insight.id,
      created_at: insight.created_at
    });
    setShowAnalysisModal(true);
  };

  const getInsightForResult = (resultId: string) => {
    return insights?.find(insight => insight.big_five_result_id === resultId);
  };

  // Filter and search results
  const filteredResults = previousResults?.filter(result => {
    const insight = getInsightForResult(result.id);
    const matchesSearch = searchTerm === '' || 
      formatDate(result.createdAt).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'with-analysis' && insight) ||
      (filterType === 'without-analysis' && !insight);
    
    return matchesSearch && matchesFilter;
  }) || [];

  // Group results
  const recentResults = filteredResults.slice(0, 5);
  const allResults = filteredResults;
  const withAnalysis = filteredResults.filter(result => getInsightForResult(result.id));

  const analysisCount = insights?.length || 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 relative">
            <History className="h-4 w-4" />
            Resultados Anteriores
            {analysisCount > 0 && (
              <Badge className="ml-2 bg-purple-600 text-white text-xs">
                {analysisCount} IA
              </Badge>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Histórico Completo de Análises
              <Badge variant="outline" className="ml-2">
                {previousResults?.length || 0} testes
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-600" />
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Todos</option>
                <option value="with-analysis">Com Análise IA</option>
                <option value="without-analysis">Sem Análise IA</option>
              </select>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="recent" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Recentes ({recentResults.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Todos ({allResults.length})
              </TabsTrigger>
              <TabsTrigger value="analysis" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Com IA ({withAnalysis.length})
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="max-h-[55vh] w-full mt-4">
              <TabsContent value="recent" className="space-y-4">
                {isLoading ? (
                  <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : recentResults.length > 0 ? (
                  <ResultsList results={recentResults} insights={insights} onViewAnalysis={handleViewAnalysis} />
                ) : (
                  <EmptyState message="Nenhum resultado recente encontrado" />
                )}
              </TabsContent>

              <TabsContent value="all" className="space-y-4">
                {allResults.length > 0 ? (
                  <ResultsList results={allResults} insights={insights} onViewAnalysis={handleViewAnalysis} />
                ) : (
                  <EmptyState message="Nenhum resultado encontrado" />
                )}
              </TabsContent>

              <TabsContent value="analysis" className="space-y-4">
                {withAnalysis.length > 0 ? (
                  <ResultsList results={withAnalysis} insights={insights} onViewAnalysis={handleViewAnalysis} />
                ) : (
                  <EmptyState message="Nenhuma análise IA encontrada" />
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {showAnalysisModal && selectedAnalysis && selectedInsight && (
        <EnhancedAdvancedAnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          analysis={selectedAnalysis}
          insight={selectedInsight}
        />
      )}
    </>
  );
}

// Component for displaying results list
function ResultsList({ results, insights, onViewAnalysis }: any) {
  const getInsightForResult = (resultId: string) => {
    return insights?.find((insight: any) => insight.big_five_result_id === resultId);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPersonalityTrait = (trait: string, value: number) => {
    const percentage = Math.round(value * 20);
    let level = 'Baixo';
    let color = 'bg-red-100 text-red-800';
    
    if (percentage >= 70) {
      level = 'Alto';
      color = 'bg-green-100 text-green-800';
    } else if (percentage >= 40) {
      level = 'Médio';
      color = 'bg-yellow-100 text-yellow-800';
    }
    
    return { level, color, percentage };
  };

  const traitNames = {
    openness: 'Abertura',
    conscientiousness: 'Conscienciosidade',
    extraversion: 'Extroversão',
    agreeableness: 'Amabilidade',
    neuroticism: 'Neuroticismo'
  };

  return (
    <div className="space-y-4">
      {results.map((result: any, index: number) => {
        const insight = getInsightForResult(result.id);
        
        return (
          <Card key={result.id} className="relative">
            {index === 0 && (
              <Badge className="absolute top-2 right-2 bg-purple-100 text-purple-800">
                Mais Recente
              </Badge>
            )}
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-4 w-4" />
                {formatDate(result.createdAt)}
                {insight && (
                  <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Análise IA
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                {Object.entries(traitNames).map(([key, name]) => {
                  const value = result[key as keyof typeof result] as number;
                  const trait = getPersonalityTrait(key, value);
                  
                  return (
                    <div key={key} className="text-center">
                      <h4 className="font-medium text-sm mb-2">{name}</h4>
                      <div className="space-y-2">
                        <Badge className={trait.color}>
                          {trait.level}
                        </Badge>
                        <div className="text-xs text-gray-600">
                          {trait.percentage}%
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${trait.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {insight && (
                <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                  <Button
                    onClick={() => onViewAnalysis(insight)}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    <Brain className="mr-2 h-4 w-4" />
                    Ver Relatório Ultra-Detalhado (o3)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Component for empty states
function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8">
      <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {message}
      </h3>
      <p className="text-gray-600">
        Complete testes para ver o histórico aqui.
      </p>
    </div>
  );
}
